import React from 'react';
import { formatBackendEnumLabel, formatPlayerRoleLabel } from '../../../../helper/helper';
import type { IJoinLeagueRequest } from '../types/request';

interface PlayerRequestsListViewProps {
  requests: IJoinLeagueRequest[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  getRequestId: (r: IJoinLeagueRequest) => string;
}

const getInitials = (firstName?: string, lastName?: string, name?: string): string => {
  if (firstName || lastName) {
    const f = (firstName ?? '').trim().charAt(0).toUpperCase();
    const l = (lastName ?? '').trim().charAt(0).toUpperCase();
    if (f && l) return `${f}${l}`;
    if (f) return f;
    if (l) return l;
  }
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return name.trim().charAt(0).toUpperCase() || '-';
  }
  return '-';
};

export const PlayerRequestsListView: React.FC<PlayerRequestsListViewProps> = ({
  requests,
  selectedIds,
  onToggleSelect,
  getRequestId,
}) => {
  return (
    <div className="player-requests-list-wrap">
      <table className="player-requests-list-table">
        <thead>
          <tr>
            <th className="player-requests-list-th-checkbox" />
            <th>PLAYER</th>
            <th>TEAM ID</th>
            <th>ROLE</th>
            <th>BATTING STYLE</th>
            <th>BOWLING STYLE</th>
            <th>ACTION</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((req) => {
            const reqExt = req as IJoinLeagueRequest & {
              playerFirstName?: string;
              playerLastName?: string;
              playerRole?: string;
              battingStyle?: string;
              bowlingStyle?: string;
              imageUri?: string;
            };
            const fullName =
              [reqExt.playerFirstName, reqExt.playerLastName].filter(Boolean).join(' ') ||
              reqExt.teamName ||
              reqExt.teamSnapshotName ||
              '-';
            const teamId = reqExt.teamCode ?? reqExt.teamSnapshotId ?? reqExt.teamId ?? '';
            const role = formatPlayerRoleLabel(
              reqExt.playerRole ?? reqExt.role ?? reqExt.teamRole ?? '-'
            );
            const batting = formatBackendEnumLabel(reqExt.battingStyle ?? '-');
            const bowling = formatBackendEnumLabel(reqExt.bowlingStyle ?? '-');
            const id = getRequestId(req);
            const imageUri = reqExt.imageUri ?? reqExt.imageUrl;
            const cdnBaseUrl = import.meta.env.VITE_IMAGE_CDN_BASE_URL;
            const avatarSrc =
              imageUri && cdnBaseUrl
                ? `${cdnBaseUrl.replace(/\/$/, '')}/${String(imageUri).replace(/^\//, '')}`
                : null;
            const initials = getInitials(
              reqExt.playerFirstName,
              reqExt.playerLastName,
              reqExt.teamName
            );

            return (
              <tr
                key={id}
                className={selectedIds.has(id) ? 'player-requests-list-row-selected' : ''}
              >
                <td className="player-requests-list-td-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(id)}
                    onChange={() => onToggleSelect(id)}
                    aria-label={`Select ${fullName}`}
                  />
                </td>
                <td>
                  <div className="player-requests-list-player-cell">
                    <div className="player-requests-list-avatar">
                      {avatarSrc ? (
                        <img src={avatarSrc} alt="" />
                      ) : (
                        <span className="player-requests-list-avatar-initials">
                          {initials}
                        </span>
                      )}
                    </div>
                    <span className="player-requests-list-name">{fullName}</span>
                  </div>
                </td>
                <td>
                  <span className="player-requests-list-team-id">{teamId}</span>
                </td>
                <td>
                  <span className="player-requests-list-value">{role}</span>
                </td>
                <td>
                  <span className="player-requests-list-value">{batting}</span>
                </td>
                <td>
                  <span className="player-requests-list-value">{bowling}</span>
                </td>
                <td>
                  <button type="button" className="player-requests-list-view-profile">
                    View Profile
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
